/**
 * "What was being said right now" — renders the transcript turns up to the
 * playhead as a caption feed. A capture indicator reflects whether any audio
 * segment covers the playhead. Clicking a turn seeks the playhead to it (same
 * contract as MomentFeed). The feed follows playback only while the user is
 * at the bottom — scrolling up to read earlier context suspends following,
 * scrolling back down resumes it.
 */
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn, IconButton, Swipe } from "@scribenote/brian-design-system";
import { formatTime } from "@utils/format-date";
import { useEffect, useRef } from "react";

import type { ReplaySegment, ReplayTurn } from "./replayClient";
import { ms } from "./timeScale";

/** Split a "Speaker: line" turn into its speaker chip + text. */
const splitSpeaker = (
  content: string,
): { speaker: string | null; text: string } => {
  const match = /^([A-Za-z ]{2,12}):\s+(.*)$/.exec(content);
  return match
    ? { speaker: match[1], text: match[2] }
    : { speaker: null, text: content };
};

/** How close to the bottom (px) still counts as "following playback". */
const FOLLOW_SLACK = 40;

export const TranscriptPanel = ({
  turns,
  capture,
  playhead,
  onSeek,
  onCollapse,
}: {
  turns: ReplayTurn[];
  capture: ReplaySegment[];
  playhead: number;
  onSeek: (t: number) => void;
  onCollapse: () => void;
}) => {
  const spoken = turns.filter((t) => ms(t.at) <= playhead);
  const capturing = capture.some(
    (s) => ms(s.start) <= playhead && playhead <= ms(s.end),
  );

  const endRef = useRef<HTMLDivElement>(null);
  const followRef = useRef(true);
  useEffect(() => {
    if (followRef.current)
      endRef.current?.scrollIntoView({
        block: "nearest",
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
      });
  }, [spoken.length]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* min-h mirrors the moment feed's "Moments" head, keeping the rule
          across both panes level however tall the capture indicator gets. */}
      <div className="flex min-h-11 items-center gap-2 border-b border-rule px-3">
        <span className="text-annotation-caps text-annotation">Transcript</span>
        <span role="status" className="ml-auto">
          {capturing ? (
            // Recording is the working state, said in a word wearing the
            // green highlighter - the atlas's own "Recording" swipe.
            <span aria-label="Audio capture active at this point">
              <Swipe tone="working" className="text-annotation-caps">
                Capturing
              </Swipe>
            </span>
          ) : capture.length === 0 ? (
            // Zero segments means no signal stream at all (no room stays, or
            // no recordings) — that's absent coverage data, not known silence.
            <span className="text-[11px] text-annotation">
              no coverage data
            </span>
          ) : (
            // Nothing being captured is no claim at all, so the word goes
            // unmarked - a wash that says "nothing happening" has nothing
            // to say.
            <span className="text-[11px] text-annotation">silent</span>
          )}
        </span>
        <IconButton
          size="sm"
          onClick={onCollapse}
          aria-expanded={true}
          aria-label="Hide transcript"
          title="Hide transcript"
        >
          <FontAwesomeIcon
            icon={faChevronRight}
            aria-hidden="true"
            className="text-xs"
          />
        </IconButton>
      </div>
      <div
        onScroll={(e) => {
          const el = e.currentTarget;
          followRef.current =
            el.scrollHeight - el.scrollTop - el.clientHeight < FOLLOW_SLACK;
        }}
        className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto p-3"
      >
        {spoken.length === 0 && (
          <span className="text-xs text-annotation italic">
            No audio captured yet at this point of the visit.
          </span>
        )}
        {spoken.map((turn, i) => {
          const { speaker, text } = splitSpeaker(turn.content);
          const isLatest = i === spoken.length - 1;
          return (
            <button
              key={`${turn.at}-${i}`}
              type="button"
              onClick={() => onSeek(ms(turn.at))}
              className={cn(
                "flex cursor-pointer items-start gap-2 rounded-paper text-left text-sm",
                "transition-colors hover:bg-recess motion-reduce:transition-none",
                "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent",
                isLatest ? "text-ink" : "text-annotation",
              )}
            >
              <span className="mt-0.5 shrink-0 font-mono text-[10px] tabular-nums text-annotation">
                {formatTime(turn.at)}
              </span>
              {speaker && (
                // Who spoke is a fact, printed as the annotation it is - a
                // person is never a status, so no wash and no hue coding.
                <span className="mt-0.5 shrink-0 text-annotation-caps text-annotation">
                  {speaker}
                </span>
              )}
              <span className="min-w-0">{text}</span>
            </button>
          );
        })}
        <div ref={endRef} />
      </div>
    </div>
  );
};
